import BackendService from 'BackendService';
import React, { useCallback, useEffect, useState } from 'react';
import type { IRRData } from '../../models/IRRDataPoint';
import type { IRRUpdate } from '../../models/IRRUpdate';

type ToastType = 'error' | 'warning' | 'info' | 'success';

export function useCashflowReportData(
  fundOpportunityId: string,
  showToast: (message: string, type: ToastType) => void,
) {
  const [irrUpdates, setIrrUpdates] = useState<IRRUpdate[]>([]);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [irrData, setIrrData] = useState<IRRData>();
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  const loadIRRData = useCallback(
    async (label: string, irrUpdate?: IRRUpdate) => {
      const update = irrUpdate ?? irrUpdates.find((u) => u.label === label);
      if (!update) return;
      try {
        setDataLoading(true);
        const data = await new BackendService().getParsedIRRDataForLabel(
          fundOpportunityId,
          update.month,
        );
        setIrrData(data);
      } catch (err) {
        console.error('Failed to load IRR data:', err);
        const message = err instanceof Error ? err.message : String(err);
        showToast(`Failed to load IRR data: ${message}`, 'error');
      } finally {
        setDataLoading(false);
      }
    },
    [fundOpportunityId, irrUpdates, showToast],
  );

  const loadIRRUpdates = useCallback(async () => {
    try {
      setLoading(true);
      const updates = await new BackendService().getIRRUpdates(fundOpportunityId);
      setIrrUpdates(updates);
      const latest = updates[updates.length - 1];
      setSelectedLabel(latest?.label ?? '');
      setLoading(false);
      if (latest) void loadIRRData(latest.label, latest);
    } catch (err) {
      console.error('Failed to load IRR updates:', err);
      setLoading(false);
      showToast('Failed to load IRR updates', 'error');
    }
    // The latest update is passed directly, so the previous irrUpdates closure is unused.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundOpportunityId, showToast]);

  useEffect(() => {
    void loadIRRUpdates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundOpportunityId]);

  const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const label = event.target.value;
    setSelectedLabel(label);
    void loadIRRData(label);
  };
  return {
    irrUpdates,
    selectedLabel,
    irrData,
    loading,
    dataLoading,
    loadIRRUpdates,
    handleMonthChange,
  };
}
